<template>
  <div class="min-h-screen bg-gray-50/30">
    <AppHeader />
    
    <!-- Hero Section -->
    <section class="relative overflow-hidden pt-32 pb-20 lg:pt-48 lg:pb-32">
      <!-- Background Decorations -->
      <div class="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10">
        <div class="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-100/40 rounded-full blur-[120px] animate-pulse"></div>
        <div class="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary-100/40 rounded-full blur-[120px] animate-pulse" style="animation-delay: 2s"></div>
      </div>

      <div class="container-custom relative">
        <div v-if="loading" class="text-center">
          <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
        </div>
        <div v-else class="text-center">
          <div class="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-sm font-medium mb-6">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
            </span>
            <span>Always Online</span>
          </div>
          <h1 class="font-heading text-5xl lg:text-7xl font-bold text-gray-900 mb-8 tracking-tight">
            {{ heroSection?.data?.title || 'Get In Touch' }}
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {{ heroSection?.data?.subtitle || 'We\'d love to hear from you. Let\'s discuss how we can help your business grow and achieve excellence.' }}
          </p>
        </div>
      </div>
    </section>
    
    <!-- Contact Info Cards Grid -->
    <section class="pb-32">
      <div class="container-custom">
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <SkeletonLoader v-for="i in 3" :key="i" type="card" height="280px" />
        </div>

        <div v-else class="space-y-20">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <!-- Email Card -->
            <a 
              :href="`mailto:${contactInfo.email}`"
              class="group relative p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
            >
              <div class="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div class="w-16 h-16 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500">
                <EmailIcon class="w-8 h-8 text-blue-600" />
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-2">Email Us</h3>
              <p class="text-gray-500 mb-6 italic">Typically responds within 2 hours</p>
              <div class="flex items-center text-blue-600 font-semibold group/link">
                <span>{{ contactInfo.email || 'contact@ruxzom.com' }}</span>
                <svg class="w-4 h-4 ml-2 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </div>
            </a>
            
            <!-- WhatsApp Card -->
            <a 
              v-if="contactInfo.whatsapp"
              :href="`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`"
              target="_blank"
              rel="noopener noreferrer"
              class="group relative p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
            >
              <div class="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-emerald-500 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div class="w-16 h-16 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500">
                <svg class="w-8 h-8 text-emerald-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-2">WhatsApp</h3>
              <p class="text-gray-500 mb-6">Quick chat for instant query</p>
              <div class="flex items-center text-emerald-600 font-semibold group/link">
                <span>{{ contactInfo.whatsapp }}</span>
                <svg class="w-4 h-4 ml-2 group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </div>
            </a>

            <!-- Office Card -->
            <div class="group relative p-8 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
              <div class="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-purple-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div class="w-16 h-16 bg-purple-50 border border-purple-100 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500">
                <LocationIcon class="w-8 h-8 text-purple-600" />
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-2">Visit Office</h3>
              <p class="text-gray-500 mb-4">{{ contactInfo.address || '123 Business St, City, State' }}</p>
              <div v-if="contactInfo.office_hours" class="flex items-center text-sm font-medium text-purple-600">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span>{{ contactInfo.office_hours }}</span>
              </div>
            </div>
          </div>

          <!-- Social Links Section -->
          <div v-if="hasSocialLinks" class="bg-gray-900 rounded-[3rem] p-12 lg:p-20 relative overflow-hidden">
            <!-- Pattern -->
            <div class="absolute inset-0 opacity-[0.03]" style="background-image: radial-gradient(#fff 1px, transparent 1px); background-size: 20px 20px;"></div>
            
            <div class="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
              <div class="max-w-xl text-center lg:text-left">
                <h2 class="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">Connect across platforms</h2>
                <p class="text-gray-400 text-lg">Follow us on social media for latest updates, projects, and behind-the-scenes content.</p>
              </div>
              
              <div class="flex flex-wrap justify-center gap-4">
                <a 
                  v-if="socialLinks.facebook"
                  :href="socialLinks.facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-blue-600 hover:border-blue-600 transition-all duration-300 group"
                  title="Facebook"
                >
                  <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a 
                  v-if="socialLinks.twitter"
                  :href="socialLinks.twitter"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-sky-600 hover:border-sky-600 transition-all duration-300 group"
                  title="Twitter"
                >
                   <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
                <a 
                  v-if="socialLinks.linkedin"
                  :href="socialLinks.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-blue-600 hover:border-blue-600 transition-all duration-300 group"
                  title="LinkedIn"
                >
                  <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
                <a 
                  v-if="socialLinks.instagram"
                  :href="socialLinks.instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-pink-600 hover:border-pink-600 transition-all duration-300 group"
                  title="Instagram"
                >
                  <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a 
                  v-if="socialLinks.youtube"
                  :href="socialLinks.youtube"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-red-600 hover:border-red-600 transition-all duration-300 group"
                  title="YouTube"
                >
                  <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <AppFooter />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useContentStore } from '@/stores/content'
import { useSettingsStore } from '@/stores/settings'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'
import { 
  EmailIcon, 
  LocationIcon 
} from '@/assets/icons'

const contentStore = useContentStore()
const settingsStore = useSettingsStore()
const loading = ref(true)

onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('contact'),
      settingsStore.fetchSettings()
    ])
  } catch (error) {
    console.error('Error loading contact page content:', error)
  } finally {
    loading.value = false
  }
})

const contactSections = computed(() => contentStore.pageSections.contact || [])
const heroSection = computed(() => contactSections.value.find(s => s.section === 'hero'))

const contactInfo = computed(() => settingsStore.getContactInfo())
const socialLinks = computed(() => settingsStore.getSocialLinks())

const hasSocialLinks = computed(() => {
  const social = socialLinks.value
  return Object.values(social).some(link => link && link.trim() !== '')
})
</script>

<style scoped>
.container-custom {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
</style>
